/**
 * AuthProvider — Superall session context.
 *
 * Owns the access token, refresh token, and `PlatformUser` payload in
 * React state, mirrored to localStorage so the rest of the app (and
 * helpers called outside the React tree, e.g. `apiFetch`, `handoffToBU`)
 * see a consistent view.
 *
 * Responsibilities (extracted from the monolithic `App.tsx`):
 *   1. `login(token, refresh, user)` — store + set state.
 *   2. `logout()` — best-effort POST /sso/revoke, then clear.
 *   3. Silent refresh — schedule a `refreshSuperallToken` call 5 min
 *      before the access token expires; on failure, clear and let
 *      `<RequireAuth>` bounce the user to `/login`.
 *   4. Listen for the `superall:auth-expired` window event dispatched
 *      by `apiFetch` on a 401 response; clear the session.
 */

import * as React from "react";

import {
  PLATFORM_API,
  AUTH_EXPIRED_EVENT,
  apiFetch,
} from "../lib/api";

export interface PlatformUser {
  id: number;
  email: string;
  fullName: string;
  avatarUrl: string | null;
  isSuperAdmin: boolean;
}

const TOKEN_KEY = "superall_token";
const REFRESH_KEY = "superall_refresh_token";
const USER_KEY = "superall_user";

const REFRESH_LEAD_MS = 5 * 60 * 1000; // 5 minutes

interface AuthContextValue {
  token: string | null;
  user: PlatformUser | null;
  isLoading: boolean;
  login: (token: string, refreshToken: string, user: PlatformUser) => void;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

function readStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

function readStoredUser(): PlatformUser | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as PlatformUser) : null;
  } catch {
    return null;
  }
}

function storeSession(
  token: string,
  refreshToken: string,
  user: PlatformUser,
) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(REFRESH_KEY, refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

/**
 * Decode the `exp` claim of a JWT (seconds since epoch) without
 * verifying the signature — only the server is trusted with the claim;
 * the client uses it solely to schedule a silent refresh.
 */
function getTokenExpMs(token: string): number | null {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(
      b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), "="),
    );
    const claims = JSON.parse(json) as { exp?: number };
    return typeof claims.exp === "number" ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

async function refreshSuperallToken(): Promise<
  { token: string; refreshToken: string } | null
> {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return null;
  try {
    const res = await fetch(`${PLATFORM_API}/sso/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      token: string;
      refreshToken: string;
    };
    if (!data.token || !data.refreshToken) return null;
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(REFRESH_KEY, data.refreshToken);
    return data;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = React.useState<string | null>(readStoredToken);
  const [user, setUser] = React.useState<PlatformUser | null>(readStoredUser);
  // `isLoading` covers the brief boot window where we might be
  // hydrating from storage; kept here for parity with the manifest.
  // Currently always false — there is no async hydration step.
  const isLoading = false;

  const logout = React.useCallback(() => {
    const current = readStoredToken();
    if (current) {
      // Best-effort revocation; we don't await because the user is
      // already on their way out and a slow network shouldn't block.
      void apiFetch(`${PLATFORM_API}/sso/revoke`, {
        method: "POST",
        body: JSON.stringify({ token: current, reason: "logout" }),
        silentOn401: true,
      }).catch(() => undefined);
    }
    clearSession();
    setToken(null);
    setUser(null);
  }, []);

  const login = React.useCallback(
    (next: string, refresh: string, nextUser: PlatformUser) => {
      storeSession(next, refresh, nextUser);
      setToken(next);
      setUser(nextUser);
    },
    [],
  );

  // Silent refresh — fires 5 min before exp; rotates token in storage
  // and React state. On failure, force re-login.
  React.useEffect(() => {
    if (!token) return undefined;
    const expMs = getTokenExpMs(token);
    if (!expMs) return undefined;
    const delay = Math.max(0, expMs - Date.now() - REFRESH_LEAD_MS);

    const timer = window.setTimeout(async () => {
      const next = await refreshSuperallToken();
      if (next) {
        setToken(next.token);
      } else {
        clearSession();
        setToken(null);
        setUser(null);
      }
    }, delay);

    return () => window.clearTimeout(timer);
  }, [token]);

  // Listen for 401 dispatches from `apiFetch`. We don't navigate from
  // here — `<RequireAuth>` handles the redirect when `token` flips to
  // null — but we do clear the session.
  React.useEffect(() => {
    function handler() {
      clearSession();
      setToken(null);
      setUser(null);
    }
    window.addEventListener(AUTH_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({ token, user, isLoading, login, logout }),
    [token, user, isLoading, login, logout],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
